import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';
import { capitalize } from '../../lib/String';
import { composeUsername } from '../../lib/DisplayName';
import { formatPhoneNumber } from '../../lib/PhoneNumber';
import { GlobalConfigurationContext } from '../../lib/context/GlobalConfigurationContext';
import { handleAutofocus } from '../../lib/TextField';
import { identifyTools } from '../../tools/Identity';
import { profileUserFields } from '../../graphql/User/UserFields';
import { UserSchema } from '../../definitions/schemas';
import FormValidatorHelper from '../../Services/FormValidatorService';
import PhoneField from '../PhoneField';
import PhotoEditor from '../PhotoEditor';
import SnackbarStore from '../../stores/observables/SnackbarStore';
import UserInterface from '../../graphql/User/UserInterface';
import UserStore from '../../stores/observables/UserStore';

interface Props {
  onClose: () => void;
  onSave: (user: UserSchema) => void;
  open: boolean;
  user: Partial<UserSchema>;
}

const ProfileEditor: React.FC<Props> = (props: Props): JSX.Element => {
  const { userAuth } = useContext(GlobalConfigurationContext);

  const { onClose, onSave, open, user } = props;

  const { current: validator } = useRef(new FormValidatorHelper());

  const [email, setEmail] = useState('');
  const [id, setId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [photo, setPhoto] = useState('');

  // misc
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const userInterface = new UserInterface();

  const currentUser = UserStore.get();
  const isCurrentUser = user?.id === currentUser?.id;

  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setDirty(false);
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setId(user.id || '');
    setEmail(user?.email || '');
    setFirstName(user?.firstName || '');
    setJobTitle(user?.member?.jobTitle || '');
    setLastName(user?.lastName || '');
    setLinkedin(user?.linkedin || '');
    setPhoneNumber(user?.phoneNumber || '');
    setPhone(formatPhoneNumber(user?.phoneNumber) || '');
    setPhoto(user?.photo || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    ev.stopPropagation();

    setSubmitted(true);

    if (!validator.isValid()) {
      return;
    }

    // Trigger the update indifferently if it is
    // the current user or another collaborator
    await updateUser();
  };

  const updateUser = async () => {
    setSaving(true);
    const payload = {
      firstName,
      id,
      jobTitle,
      lastName,
      linkedin,
      phoneNumber,
      photo,
    };
    try {
      let updatedProfile: UserSchema;

      if (isCurrentUser) {
        const response = await userInterface.updateProfile(
          payload,
          profileUserFields
        );

        updatedProfile = response.updateProfile.user;
      } else {
        const response = await userInterface.updateUser(
          { ...payload, email },
          profileUserFields
        );

        updatedProfile = response.updateUser.user;
      }

      SnackbarStore.show('User profile updated');

      setSaving(false);
      onSave(updatedProfile);
      updateCurrentUser(updatedProfile);

      onClose();
    } catch (error) {
      setSaving(false);
      SnackbarStore.show(error as string);
    }
  };

  // if current user is updated, update the data in UserStore
  const updateCurrentUser = (userUpdated: UserSchema) => {
    if (!isCurrentUser) {
      return;
    }

    const newDataUser: UserSchema = {
      ...userUpdated,
      photo: userUpdated.photo,
      firstName: userUpdated.firstName,
      lastName: userUpdated.lastName,
      fullName: userUpdated.fullName,
    };

    UserStore.set(newDataUser);
    identifyTools(newDataUser, userAuth);
  };

  const inputChangeHandler =
    (
      setter:
        | typeof setFirstName
        | typeof setLastName
        | typeof setLinkedin
        | typeof setJobTitle,
      modifier?: (...args: any[]) => string
    ) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { selectionStart } = e.target;
      let { value } = e.target;

      if (modifier) {
        value = modifier(value, false);
        // tell react to not discard the synthetic event, this is required
        // to preserve the position of the caret after modifying the value
        e.persist();
      }

      setter(value);
      setDirty(true);

      setTimeout(() => {
        // maintain the caret position where it was before the modifier applied
        // its changes to the final value
        if (modifier && e.target) {
          e.target.selectionStart = selectionStart;
          e.target.selectionEnd = selectionStart;
        }
      });
    };

  // event from react-intl-tel-input
  const onChangePhone = (e: any) => {
    const { number, fullNumber } = e.target.value;
    setPhoneNumber(fullNumber);
    setPhone(number);
  };

  const handleChangePhoto = (uri: string | undefined) => setPhoto(uri!);

  validator.check('email', email, 'required|email');
  validator.check('first name', firstName, 'required|noNumbers');
  validator.check('last name', lastName, 'required|noNumbers');
  validator.check('phone number', phoneNumber, 'phoneFormat');
  validator.check('linkedin', linkedin, 'url');

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle>Edit profile</DialogTitle>
      <form onSubmit={handleSubmit} tabIndex={-1}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box mb={1}>
                <PhotoEditor
                  fallback={(composeUsername(user) || '-')[0].toUpperCase()}
                  onPhotoChange={handleChangePhoto}
                  photo={photo}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                color="secondary"
                disabled={saving}
                error={validator.hasError('first name', submitted)}
                fullWidth
                helperText={validator.getError('first name', submitted)}
                inputRef={handleAutofocus(!!email && !firstName, dirty)}
                label="First name"
                onChange={inputChangeHandler(setFirstName)}
                required
                value={firstName}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                color="secondary"
                disabled={saving}
                error={validator.hasError('last name', submitted)}
                fullWidth
                helperText={validator.getError('last name', submitted)}
                label="Last name"
                onChange={inputChangeHandler(setLastName)}
                required
                value={lastName}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                color="secondary"
                disabled
                fullWidth
                error={validator.hasError('email', submitted)}
                helperText={validator.getError('email', submitted)}
                label="Email"
                required
                value={email}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                color="secondary"
                disabled={saving}
                fullWidth
                label="Job title"
                onChange={inputChangeHandler(setJobTitle, capitalize)}
                value={jobTitle}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <PhoneField
                disabled={saving}
                error={validator.hasError('phone number', submitted)}
                fullWidth
                helperText={validator.getError('phone number', submitted)}
                label="Phone"
                onChange={onChangePhone}
                size="medium"
                value={phone}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                color="secondary"
                disabled={saving}
                error={validator.hasError('linkedin', submitted)}
                fullWidth
                helperText={validator.getError('linkedin', submitted)}
                label="LinkedIn Profile URL"
                onChange={inputChangeHandler(setLinkedin)}
                value={linkedin}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="secondary"
            disabled={saving}
            onClick={handleSubmit}
            type="submit"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProfileEditor;
